import React from "react";
import {
  User,
  HTTP_UNAUTHORIZED,
  isUserExpired,
  isUserAnonymous,
  Profile,
  FetchOptions,
  ProfileState,
  HTTP_BAD_REQUEST,
  HTTP_FORBIDDEN,
  HTTP_METHOD_NOT_ALLOWED,
  HTTP_CONFLICT,
  HTTP_NOT_FOUND,
} from "../api";
import {
  Account,
  AccountContext,
  MakeRequestResponse,
  RetryBehavior,
} from "./AccountContext";
import { PreferencesContext } from "./PreferencesContext";
import { JwtContext } from "./JwtContext";
import {
  LiveData,
  stringOrNullToNumberOrNull,
  useLiveData,
  CLEVER_REDIRECT_PATH,
  CLASSLINK_DEFAULT_DEEP_LINK_PATH,
} from "../utils";
import { useLoadingIndicator } from "./LoadingIndicatorContext";
import {
  AB_IDENTIFIER_KEY,
  PROFILE_ID_KEY,
  QUERY_PARAM_PREFIX,
} from "./PreferencesProvider";
import { RetryContext, ShowRetry } from "./RetryContext";
import { AnalyticsContext } from "./AnalyticsContext";
import { ClientConfigContext } from "./ClientConfigContext";
import { useHistory, useLocation } from "react-router-dom";
import { t } from "i18next";

interface Props {
  children: React.ReactNode;
}

function makeUUID() {
  if (crypto?.randomUUID) {
    return crypto.randomUUID();
  }

  // Old browsers:
  return `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))
    ).toString(16)
  );
}

function getABIdentifier() {
  let identifier = localStorage.getItem(AB_IDENTIFIER_KEY);
  if (identifier !== null) {
    return identifier;
  }
  identifier = makeUUID();
  localStorage.setItem(AB_IDENTIFIER_KEY, identifier);
  return identifier;
}

function withExtraQueryParams(url: string) {
  const params = new URLSearchParams();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(QUERY_PARAM_PREFIX)) {
      continue;
    }
    const name = key.slice(QUERY_PARAM_PREFIX.length);
    const value = localStorage.getItem(key);
    if (name && value !== null) {
      params.set(name, value);
    }
  }
  const extra = params.toString();
  if (!extra) {
    return url;
  }
  return url + (url.includes("?") ? "&" : "?") + extra;
}

export const makeRequestInternal = async (
  url: string,
  method: string,
  showRetryNonSuccess?: ShowRetry,
  showRetryException?: ShowRetry,
  dropNonSuccess?: (code: number) => boolean,
  body?: any,
  extraHeaders?: any,
  fetchOptions?: FetchOptions
) => {
  const isFormData = body instanceof FormData;
  const requestUrl = withExtraQueryParams(url);
  const result = new Promise<MakeRequestResponse>((resolve, reject) => {
    function do_fetch() {
      fetch(requestUrl, {
        method,
        mode: "cors",
        cache: fetchOptions?.cache || "no-cache",
        credentials: "include",
        headers: {
          ...(extraHeaders || {}),
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          ...{ "X-AB-Identifier": getABIdentifier() },
        },
        body: isFormData ? body : JSON.stringify(body),
        ...(fetchOptions?.signal && { signal: fetchOptions?.signal }),
      })
        .then(async (response) => {
          let body: any = null;

          if (response.status !== 204 /* NO_CONTENT */) {
            if (response.headers.get("content-type") === "application/json") {
              body = await response.json();
            }
          }

          if (response.status >= 200 && response.status <= 299) {
            resolve({
              code: response.status,
              body,
              isSuccessful: true,
              headers: response.headers,
              blob: () => {
                return response.blob();
              },
              text: () => {
                return response.text();
              },
            });
          } else {
            if (!dropNonSuccess?.(response.status)) {
              if (showRetryNonSuccess) {
                showRetryNonSuccess(do_fetch);
              } else {
                resolve({
                  code: response.status,
                  errorBody: body,
                  isSuccessful: false,
                  headers: response.headers,
                  blob: () => {
                    return response.blob();
                  },
                  text: () => {
                    return response.text();
                  },
                });
              }
            }
          }
        })
        .catch(() => {
          if (showRetryException) {
            showRetryException(do_fetch);
          } else {
            reject();
          }
        });
    }
    do_fetch();
  });
  return result;
};

export const AccountProvider = (props: Props) => {
  const accountRef = React.useRef(
    new LiveData<Account>(new Account(null!, -1, 0, null, -1, 0, null))
  );
  const userLoadingJobForVersion = React.useRef(0);
  const loadingAnonymousUser = React.useRef(false);
  const profileStateLoadingJobForVersion = React.useRef(0);
  const [ready, setReady] = React.useState(false);
  const { getJwtOrNull, setJwt, getJwt } = React.useContext(JwtContext);
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { acceptData } = React.useContext(PreferencesContext);
  const { showRetry, showRetryCancellable } = React.useContext(RetryContext);
  const {
    maybeStartAnalytics,
    identifyUser,
    resetUser,
    setIncludeIP,
    registerSuperProperties,
    superPropertiesFromUser,
  } = React.useContext(AnalyticsContext);
  const { clientConfig } = React.useContext(ClientConfigContext);
  const history = useHistory();
  const makeRequestRef = React.useRef<
    (
      url: string,
      method: string,
      retryBehavior: RetryBehavior,
      sendJwt: boolean,
      body?: any,
      fetchOptions?: FetchOptions
    ) => Promise<MakeRequestResponse>
  >(undefined!);
  const fromLogin = React.useRef(false);
  const location = useLocation();

  React.useEffect(() => {
    fromLogin.current = !!location.state?.fromLogin;
  }, [location]);

  const getProfileState = React.useCallback(
    (profileId): Promise<{ state: ProfileState }> => {
      const path = `profile-state/${profileId}/`;
      return makeRequestRef
        .current(
          path,
          "GET",
          RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
          true
        )
        .then((resp) => {
          return resp.body;
        });
    },
    []
  );

  const getProfile = React.useCallback((user: User) => {
    const profileId = stringOrNullToNumberOrNull(
      localStorage.getItem(PROFILE_ID_KEY)
    );
    if (user.profiles.length === 0) {
      return null;
    } else if (isUserExpired(user)) {
      return user.profiles.find((profile) => {
        return profile.is_guest;
      });
    } else {
      return (
        user.profiles.find((profile) => {
          return profile.id === profileId;
        }) || user.profiles[user.profiles.length - 1]
      );
    }
  }, []);

  const adjustUser = React.useCallback((user: User) => {
    if (
      user.unconfirmed_email_expiration_in_ms !== undefined ||
      user.unconfirmed_email_expiration_in_ms !== null
    ) {
      return {
        ...user,
        unconfirmed_email_expiration_date: new Date(
          new Date().getTime() + Number(user.unconfirmed_email_expiration_in_ms)
        ).toISOString(),
        unconfirmed_email_expiration_in_ms: null,
      };
    } else {
      return user;
    }
  }, []);

  const loadAnonymousUser = React.useCallback(
    async (version: number) => {
      loadingAnonymousUser.current = true;
      startLoading();
      const anonUserResponse = await makeRequestRef.current(
        "anon-user/",
        "GET",
        RetryBehavior.RETRY_ALL,
        false
      );
      if (userLoadingJobForVersion.current !== version) {
        return;
      }
      stopLoading();
      setJwt(anonUserResponse.body.access_token);
      const profileStateCurrentVersion =
        accountRef.current.value.profileStateExpectedVersion + 1;
      const unadjustedUser = anonUserResponse.body.user;
      const user = adjustUser(unadjustedUser);
      accountRef.current.setValue(
        accountRef.current.value.copy({
          user: user,
          userCurrentVersion: userLoadingJobForVersion.current,
          profile: null,
          profileStateCurrentVersion: profileStateCurrentVersion,
          profileStateExpectedVersion: profileStateCurrentVersion,
          profileState: null,
        })
      );
      setIncludeIP(false);
      resetUser(user);
      registerSuperProperties(superPropertiesFromUser(user));
      loadingAnonymousUser.current = false;
    },
    [
      setJwt,
      adjustUser,
      startLoading,
      stopLoading,
      setIncludeIP,
      resetUser,
      registerSuperProperties,
      superPropertiesFromUser,
    ]
  );

  const ensureProfileState = React.useCallback(async () => {
    if (
      accountRef.current.value.profileStateCurrentVersion !==
      accountRef.current.value.profileStateExpectedVersion
    ) {
      if (
        accountRef.current.value.profile == null ||
        accountRef.current.value.profile.is_guest
      ) {
        profileStateLoadingJobForVersion.current =
          accountRef.current.value.profileStateExpectedVersion;
        accountRef.current.setValue(
          accountRef.current.value.copy({
            profileStateCurrentVersion:
              profileStateLoadingJobForVersion.current,
            profileState: null,
          })
        );
      } else {
        profileStateLoadingJobForVersion.current =
          accountRef.current.value.profileStateExpectedVersion;
        const expectedUserVersion =
          accountRef.current.value.userExpectedVersion;
        const expectedProfileStateVersion =
          accountRef.current.value.profileStateExpectedVersion;
        const profileStateResponse = await getProfileState(
          accountRef.current.value.profile.id
        );
        if (
          accountRef.current.value.userExpectedVersion !==
            expectedUserVersion ||
          accountRef.current.value.userExpectedVersion !==
            accountRef.current.value.userCurrentVersion ||
          profileStateLoadingJobForVersion.current !==
            expectedProfileStateVersion
        ) {
          return;
        }
        accountRef.current.setValue(
          accountRef.current.value.copy({
            profileStateCurrentVersion:
              profileStateLoadingJobForVersion.current,
            profileState: profileStateResponse.state,
          })
        );
      }
    }
  }, [getProfileState]);

  const agreeToTermsOfUse = React.useCallback(() => {
    acceptData();
    maybeStartAnalytics();
    const user = accountRef.current.value.user;
    if (user !== undefined) {
      setIncludeIP(user!!.has_given_verified_consent);
      identifyUser(user!!);
    }
  }, [acceptData, maybeStartAnalytics, setIncludeIP, identifyUser]);

  const login = React.useCallback(
    (access_token: string, unadjustedUser: User) => {
      const user = adjustUser(unadjustedUser);
      const userExpectedVersion =
        accountRef.current.value.userExpectedVersion + 1;
      const profileStateExpectedVersion =
        accountRef.current.value.profileStateExpectedVersion + 1;
      userLoadingJobForVersion.current = userExpectedVersion;
      loadingAnonymousUser.current = false;
      setJwt(access_token);
      accountRef.current.setValue(
        accountRef.current.value.copy({
          user: user,
          userExpectedVersion: userExpectedVersion,
          userCurrentVersion: userExpectedVersion,
          profile: getProfile(user),
          profileStateExpectedVersion: profileStateExpectedVersion,
        })
      );
      if (
        user.has_given_verified_consent ||
        (!user.is_guest &&
          !isUserAnonymous(user) &&
          !user.has_unconfirmed_email)
      ) {
        agreeToTermsOfUse();
      } else {
        setIncludeIP(false);
        identifyUser(user);
      }
      ensureProfileState();
    },
    [
      setJwt,
      adjustUser,
      ensureProfileState,
      getProfile,
      setIncludeIP,
      agreeToTermsOfUse,
      identifyUser,
    ]
  );

  const loginWithClever = React.useCallback(
    (options?: { next?: string | null; ssoRetry?: boolean }) => {
      const cleverParams = new URLSearchParams();
      cleverParams.append("response_type", "code");
      cleverParams.append(
        "redirect_uri",
        window.location.origin + CLEVER_REDIRECT_PATH
      );
      cleverParams.append("client_id", "1552c9d049f4c4bb730c");
      cleverParams.append("confirmed", "false");
      const next = options?.next ?? location.state?.next;
      if (next || options?.ssoRetry) {
        // "state" will be passed to us after user finishes logging in and is redirected back to us
        // https://dev.clever.com/docs/oauth-implementation#redirect-uri-parameters
        cleverParams.append(
          "state",
          JSON.stringify({
            ...(next ? { next } : {}),
            // Marks a re-authorization after Clever rejected a code, so LoginPage won't retry again
            ...(options?.ssoRetry ? { sso_retry: "1" } : {}),
          })
        );
      }
      const cleverAuthorizeUrl =
        "https://clever.com/oauth/authorize?" + cleverParams.toString();
      window.location.href = cleverAuthorizeUrl;
    },
    [location]
  );

  const loginWithClassLink = React.useCallback(
    (options?: { next?: string | null; ssoRetry?: boolean }) => {
      const classLinkParams = new URLSearchParams();

      // Allow a login initiated from the mobile web browser to use the deep link instead of webapp redirect_uri.
      // Non-mobile logins will still end in the webapp.
      const redirectUri =
        window.location.origin.replace(
          /^https:\/\/(.*)superteacherapp.com/,
          "https://$1getsuperteacher.com"
        ) + CLASSLINK_DEFAULT_DEEP_LINK_PATH;
      classLinkParams.append("scope", "full");
      classLinkParams.append("response_type", "code");
      classLinkParams.append("redirect_uri", redirectUri);
      const clientId =
        process.env.NODE_ENV === "production"
          ? "c1747065795962e64901fc400161c4f32f9131df6270"
          : "c1741117971040ab255aaa9bc6b78ba700a2bbea5218";
      classLinkParams.append("client_id", clientId);
      const next = options?.next ?? location.state?.next;
      if (next || options?.ssoRetry) {
        // "state" will be passed to us after user finishes logging in and is redirected back to us
        // https://help.classlink.com/s/article/Requesting-Authorization-for-Access
        classLinkParams.append(
          "state",
          JSON.stringify({
            ...(next ? { next } : {}),
            // Marks a re-authorization after ClassLink rejected a code, so LoginPage won't retry again
            ...(options?.ssoRetry ? { sso_retry: "1" } : {}),
          })
        );
      }
      const cleverAuthorizeUrl =
        "https://launchpad.classlink.com/oauth2/v2/auth?" +
        classLinkParams.toString();
      window.location.href = cleverAuthorizeUrl;
    },
    [location]
  );

  const updateUser = React.useCallback(
    (
      unadjustedUser: User,
      skipRefreshingProfileState: boolean,
      switchToProfileId: number | null = null,
      clearProfile: boolean = false
    ) => {
      const user = adjustUser(unadjustedUser);
      const userExpectedVersion =
        accountRef.current.value.userExpectedVersion + 1;
      const profileStateExpectedVersion =
        accountRef.current.value.profileStateExpectedVersion +
        (skipRefreshingProfileState ? 0 : 1);
      userLoadingJobForVersion.current = userExpectedVersion;
      loadingAnonymousUser.current = false;
      if (clearProfile) {
        localStorage.removeItem(PROFILE_ID_KEY);
      }
      if (switchToProfileId) {
        localStorage.setItem(PROFILE_ID_KEY, switchToProfileId.toString());
      }
      accountRef.current.setValue(
        accountRef.current.value.copy({
          user: user,
          userExpectedVersion: userExpectedVersion,
          userCurrentVersion: userExpectedVersion,
          profile: getProfile(user),
          profileStateExpectedVersion: profileStateExpectedVersion,
        })
      );
      // We can't just check skipRefreshingProfileState as we could have been in process of a
      // profile state refresh even before this function invocation.
      if (
        accountRef.current.value.profileStateCurrentVersion !==
        accountRef.current.value.profileStateExpectedVersion
      ) {
        ensureProfileState();
      }
    },
    [ensureProfileState, adjustUser, getProfile]
  );

  const updateProfile = React.useCallback(
    (profile: Profile) => {
      const user = accountRef.current.value.user;
      updateUser(
        {
          ...user,
          profiles: user.profiles.map((oldProfile) =>
            oldProfile.id === profile.id ? profile : oldProfile
          ),
        },
        true
      );
    },
    [updateUser]
  );

  const addProfile = React.useCallback(
    (profile: Profile) => {
      const user = accountRef.current.value.user;
      const newProfiles = user.profiles.concat(profile);
      updateUser(
        {
          ...user,
          profiles: newProfiles,
        },
        false,
        profile.id
      );
    },
    [updateUser]
  );

  const removeProfile = React.useCallback(
    (profile: Profile) => {
      const user = accountRef.current.value.user;
      const newProfiles = user.profiles.filter((p) => {
        return p.id !== profile.id;
      });
      updateUser(
        {
          ...user,
          profiles: newProfiles,
        },
        false
      );
    },
    [updateUser]
  );

  const ensureAccount = React.useCallback(async () => {
    if (
      accountRef.current.value.userCurrentVersion ===
      accountRef.current.value.userExpectedVersion
    ) {
      ensureProfileState();
    } else {
      const expectedVersion = (userLoadingJobForVersion.current =
        accountRef.current.value.userExpectedVersion);
      loadingAnonymousUser.current = false;
      let jwt = getJwtOrNull();
      let needsAnonToken = true;
      if (jwt) {
        startLoading();
        let currentUserResponse = await makeRequestRef.current(
          "current-user/",
          "GET",
          RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
          true
        );
        if (userLoadingJobForVersion.current !== expectedVersion) {
          return;
        }
        const unadjustedUser = currentUserResponse.body;
        if (unadjustedUser) {
          const user = adjustUser(unadjustedUser);
          stopLoading();
          accountRef.current.setValue(
            accountRef.current.value.copy({
              user: user,
              userCurrentVersion: userLoadingJobForVersion.current,
              profile: getProfile(user),
              profileStateExpectedVersion:
                accountRef.current.value.profileStateExpectedVersion + 1,
            })
          );
          if (isUserAnonymous(user)) {
            setIncludeIP(false);
            resetUser(user);
            registerSuperProperties(superPropertiesFromUser(user));
          } else {
            setIncludeIP(user.has_given_verified_consent);
            identifyUser(user);
          }
          ensureProfileState();
          needsAnonToken = false;
        } else if (currentUserResponse.code !== HTTP_UNAUTHORIZED) {
          showRetry(() => {
            ensureAccount();
          });
          return;
        }
      }
      if (needsAnonToken) {
        // TODO: maybe you have been signed out dialog
        loadAnonymousUser(expectedVersion);
      }
    }
  }, [
    getJwtOrNull,
    adjustUser,
    loadAnonymousUser,
    ensureProfileState,
    startLoading,
    stopLoading,
    getProfile,
    showRetry,
    identifyUser,
    resetUser,
    setIncludeIP,
    registerSuperProperties,
    superPropertiesFromUser,
  ]);

  React.useEffect(() => {
    ensureAccount();
  }, [ensureAccount]);

  const logout = React.useCallback(async () => {
    const userExpectedVersion =
      accountRef.current.value.userExpectedVersion + 1;
    accountRef.current.setValue(
      accountRef.current.value.copy({
        userExpectedVersion: userExpectedVersion,
      })
    );
    userLoadingJobForVersion.current = userExpectedVersion;
    loadAnonymousUser(userExpectedVersion);
  }, [loadAnonymousUser]);

  const forceReloadUserAndProfile = React.useCallback(() => {
    accountRef.current.setValue(
      accountRef.current.value.copy({
        userExpectedVersion: accountRef.current.value.userExpectedVersion + 1,
      })
    );
    ensureAccount();
  }, [ensureAccount]);

  const forceReloadProfileState = React.useCallback(() => {
    accountRef.current.setValue(
      accountRef.current.value.copy({
        profileStateExpectedVersion:
          accountRef.current.value.profileStateExpectedVersion + 1,
      })
    );
    ensureProfileState();
  }, [ensureProfileState]);

  const makeRequest = React.useCallback(
    async (
      url: string,
      method: string,
      retryBehavior: RetryBehavior,
      sendJwt: boolean,
      body?: any,
      fetchOptions?: FetchOptions
    ) => {
      const apiRoot = clientConfig.api_base_url;

      if (url.startsWith(apiRoot)) {
        // URLs for e.g. images returned by the backend may contain the domain, we remove it here.
        url = url.replace(apiRoot, "");
      }

      switch (retryBehavior) {
        case RetryBehavior.RETRY_ALL:
          return makeRequestInternal(
            apiRoot + url,
            method,
            showRetry,
            showRetry,
            undefined,
            body,
            sendJwt
              ? {
                  Authorization: `Bearer ${getJwt()}`,
                }
              : undefined,
            fetchOptions
          );
        case RetryBehavior.RETRY_ALL_CANCELLABLE:
          return makeRequestInternal(
            apiRoot + url,
            method,
            showRetryCancellable,
            showRetryCancellable,
            undefined,
            body,
            sendJwt
              ? {
                  Authorization: `Bearer ${getJwt()}`,
                }
              : undefined,
            fetchOptions
          );
        case RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE:
          return makeRequestInternal(
            apiRoot + url,
            method,
            showRetryCancellable,
            showRetryCancellable,
            (code) => {
              if (code === HTTP_NOT_FOUND) {
                history.push("/");
                return true;
              } else if (code === HTTP_CONFLICT) {
                forceReloadUserAndProfile();
                history.push("/");
                return true;
              } else if (
                code === HTTP_UNAUTHORIZED ||
                code === HTTP_BAD_REQUEST ||
                code === HTTP_FORBIDDEN ||
                code === HTTP_METHOD_NOT_ALLOWED
              ) {
                if (!loadingAnonymousUser.current) {
                  logout();
                  history.push("/account/login/", {
                    message: t("login_required_message"),
                    ...(fromLogin.current
                      ? {}
                      : {
                          next:
                            window.location.pathname + window.location.search,
                        }),
                  });
                }
                return true;
              } else {
                return false;
              }
            },
            body,
            sendJwt
              ? {
                  Authorization: `Bearer ${getJwt()}`,
                }
              : undefined,
            fetchOptions
          );
        case RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS:
          return makeRequestInternal(
            apiRoot + url,
            method,
            undefined,
            showRetry,
            undefined,
            body,
            sendJwt
              ? {
                  Authorization: `Bearer ${getJwt()}`,
                }
              : undefined,
            fetchOptions
          );
        case RetryBehavior.EXPOSE_ALL:
          return makeRequestInternal(
            apiRoot + url,
            method,
            undefined,
            undefined,
            undefined,
            body,
            sendJwt
              ? {
                  Authorization: `Bearer ${getJwt()}`,
                }
              : undefined,
            fetchOptions
          );
      }
    },
    [
      clientConfig.api_base_url,
      showRetry,
      showRetryCancellable,
      getJwt,
      history,
      logout,
      forceReloadUserAndProfile,
    ]
  );

  makeRequestRef.current = makeRequest;

  const contextValue = React.useMemo(
    () => ({
      accountLiveData: accountRef.current,
      makeRequest,
      logout,
      login,
      loginWithClever,
      loginWithClassLink,
      updateUser,
      updateProfile,
      addProfile,
      removeProfile,
      forceReloadUserAndProfile,
      forceReloadProfileState,
      agreeToTermsOfUse,
    }),
    [
      makeRequest,
      logout,
      login,
      loginWithClever,
      loginWithClassLink,
      updateUser,
      updateProfile,
      addProfile,
      removeProfile,
      forceReloadUserAndProfile,
      forceReloadProfileState,
      agreeToTermsOfUse,
    ]
  );

  useLiveData(
    React.useCallback(
      (account: Account) => {
        if (account.hasUserAndMaybeProfile()) {
          setReady(true);
          maybeStartAnalytics();
        }
      },
      [maybeStartAnalytics]
    ),
    accountRef.current
  );

  return ready ? (
    <AccountContext.Provider value={contextValue}>
      {props.children}
    </AccountContext.Provider>
  ) : (
    <></>
  );
};
